"use client";
import {
  DatePicker,
  Input,
  SelectInput,
  SelectSearchInput,
  AutoExpandTextArea,
} from "@/components";
import { MultiSelectField } from "@/components/ui/forms/MultiSelectField";
import { useSkills, useSpecialties } from "@/hooks";
import { OptionsJobber } from "@/types";
import { useEffect, useMemo, useState, useLayoutEffect } from "react";
import { useFormContext } from "react-hook-form";
import { LuChevronDown, LuChevronUp, LuTrash2, LuTriangleAlert, LuInfo } from "react-icons/lu";
import { ReferenceForm } from "./ReferenceForm";
import { NoReferenceWarningModal } from "@/components/ui/NoReferenceWarningModal";
import { SessionStorage, SESSION_STORAGE_KEYS } from "@/utils/sessionStorage";
import { useTranslations } from "next-intl";

// Simple confirmation modal component
const ConfirmDeleteModal = ({
  isOpen,
  onConfirm,
  onCancel,
  title,
  deleteType,
}: {
  isOpen: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  title: string;
  deleteType: 'deleteExperience' | 'deleteEducation';
}) => {
  const tCommon = useTranslations("jobberProfile.forms.stepThree.common");
  const tDeleteModal = useTranslations("jobberProfile.forms.stepThree.deleteModal");
  
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-sm w-full mx-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          {tDeleteModal(deleteType)}
        </h3>
        <p className="text-gray-600 mb-6">
          {tDeleteModal('confirmMessage', {title})}
        </p>
        <div className="flex gap-3 justify-end">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            {tCommon("cancel")}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
          >
            {tCommon("delete")}
          </button>
        </div>
      </div>
    </div>
  );
};

type ExperienceFormProp = {
  index: number;
  optionsJobber: {
    options?: OptionsJobber;
    optionError: boolean;
    optionIsLoading: boolean;
  };
  isCollapsed: boolean;
  shouldShowCollapse: boolean;
  hasData: boolean;
  onToggleCollapse: () => void;
  onRemove: () => void;
  isComplete: boolean;
  isConfirmed: boolean;
  onConfirm: () => void;
  onEdit: () => void;
  validateSignal?: number;
  confirmReminderSignal?: number;
  highlightUnconfirmed?: boolean;
};

export const ExperienceForm = ({
  index,
  optionsJobber,
  isCollapsed,
  shouldShowCollapse,
  hasData,
  onToggleCollapse,
  onRemove,
  isComplete,
  isConfirmed,
  onConfirm,
  onEdit,
  validateSignal,
  confirmReminderSignal,
  highlightUnconfirmed,
}: ExperienceFormProp) => {
  const { watch, setValue } = useFormContext();
  const [searchNameSpecialty, setSearchNameSpecialty] = useState("");
  const [searchNameSkill, setSearchNameSkill] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showNoReferenceWarning, setShowNoReferenceWarning] = useState(false);
  const [isEditing, setIsEditing] = useState(!isConfirmed);
  const [needsConfirmationHighlight, setNeedsConfirmationHighlight] = useState(false);
  
  const tCommon = useTranslations("jobberProfile.forms.stepThree.common");
  const tDeleteModal = useTranslations("jobberProfile.forms.stepThree.deleteModal");
  const tExperience = useTranslations("jobberProfile.forms.stepThree.experience");

  // Watch the selected category to filter skills and specialties
  const selectedCategory = watch(`experiences.${index}.category`);
  const categoryId = selectedCategory?.id || "";

  const {
    data: skillsData,
    fetchNextPage: fetchNextSkillsPage,
    hasNextPage: hasNextSkillsPage,
    isFetchingNextPage: isFetchingNextSkillsPage,
    isLoading: isLoadingSkills,
  } = useSkills(categoryId, searchNameSkill);

  const {
    data: specialtyData,
    fetchNextPage: fetchNextSpecialtyPage,
    hasNextPage: hasNextSpecialtyPage,
    isFetchingNextPage: isFetchingNextSpecialtyPage,
    isLoading: isLoadingSpecialty,
  } = useSpecialties(categoryId, searchNameSpecialty);

  const jobTitle =
    watch(`experiences.${index}.job_title`) || tExperience("untitledExperience");

  const referenceData = watch(`experiences.${index}.reference`);
  
  // Watch and normalize skills
  const watchedSkills = watch(`experiences.${index}.skills_experiences`);

  // Normalize skills to ensure {id, name} structure
  const normalizedSkills = useMemo(() => {
    if (!Array.isArray(watchedSkills)) return [];
    return watchedSkills.map((skill: any) => {
      // If skill already has {id, name}, return as is
      if (skill?.id && skill?.name) return skill;
      // Transform {first_id, name_first} to {id, name}
      if (skill?.first_id && skill?.name_first) {
        return { id: skill.first_id, name: skill.name_first };
      }
      return skill;
    });
  }, [watchedSkills, index]);

  // Sync normalized skills IMMEDIATELY and synchronously when they differ
  useLayoutEffect(() => {
    if (normalizedSkills.length > 0 && watchedSkills.length > 0) {
      const firstWatched = watchedSkills[0];
      const firstNormalized = normalizedSkills[0];
      // Only update if transformation occurred (has first_id but no id)
      if (firstWatched?.first_id && !firstWatched?.id && firstNormalized?.id) {
        setValue(`experiences.${index}.skills_experiences`, normalizedSkills, { shouldDirty: false, shouldTouch: false });
      }
    }
  }, [normalizedSkills, watchedSkills, index, setValue]);
  const hasReference = !!referenceData;
  // If a reference object exists at all, require all mandatory fields; empty freshly-added reference forces Incomplete.
  const referenceIncomplete = !!(referenceData && (
    !referenceData.first_name || !referenceData.last_name || !referenceData.relationship || !referenceData.email
  ));

  // Validation status now respects partial reference: stays incomplete until reference removed or completed
  const getValidationStatus = () => {
    if (isConfirmed) return 'confirmed';
    if (isComplete && !referenceIncomplete) return 'verify';
    return 'incomplete';
  };

  const validationStatus = getValidationStatus();

  // Reference conditional requirement: only if any reference field started
  const refStarted = !!(referenceData && (
    referenceData.first_name || referenceData.last_name || referenceData.relationship || referenceData.email
  ));

  const jobTitleValue = watch(`experiences.${index}.job_title`);
  const workModeValue = watch(`experiences.${index}.work_mode`);
  const companyNameValue = watch(`experiences.${index}.company_name`);
  const companyIndustryValue = watch(`experiences.${index}.company_industry`);
  const categoryValue = watch(`experiences.${index}.category`);
  const specializationValue = watch(`experiences.${index}.specialization`);
  const startDateValue = watch(`experiences.${index}.start_date`);
  const skillsValue = normalizedSkills || [];

  const specializationOptions = useMemo(
    () => specialtyData?.pages?.flatMap((page) => page.data) || [],
    [specialtyData?.pages]
  );

  const specializationDefaultItem = useMemo(() => {
    if (!specializationValue) return null;

    if (typeof specializationValue === "object") {
      const id = (specializationValue as any).id ?? (specializationValue as any).value;
      const name =
        (specializationValue as any).name ??
        (specializationValue as any).label ??
        (specializationValue as any).title ??
        "";
      if (!id || !name) return null;
      return { id: String(id), name: String(name) };
    }

    if (typeof specializationValue === "string" && specializationValue) {
      const match = specializationOptions.find(
        (item) => String(item.id) === specializationValue
      );
      if (match) {
        return { id: String(match.id), name: String(match.name) };
      }
    }

    return null;
  }, [specializationOptions, specializationValue]);

  const normaliseOptionFilled = (value: unknown) => {
    if (!value) return false;
    if (typeof value === "string") return value.trim().length > 0;
    if (typeof value === "object") {
      const option = value as { id?: string | number; value?: string | number };
      return Boolean(option?.id ?? option?.value);
    }
    return false;
  };

  const missingKeys: string[] = [];
  if (!jobTitleValue || !(`${jobTitleValue}`.trim())) missingKeys.push("job_title");
  if (!normaliseOptionFilled(workModeValue)) missingKeys.push("work_mode");
  if (!companyNameValue || !(`${companyNameValue}`.trim())) missingKeys.push("company_name");
  if (!normaliseOptionFilled(companyIndustryValue)) missingKeys.push("company_industry");
  if (!normaliseOptionFilled(categoryValue)) missingKeys.push("category");
  if (!normaliseOptionFilled(specializationValue)) missingKeys.push("specialization");
  if (!startDateValue) missingKeys.push("start_date");
  if (!Array.isArray(skillsValue) || skillsValue.length === 0) missingKeys.push("skills_experiences");

  if (referenceData) {
    if (!referenceData.first_name) missingKeys.push("reference_first_name");
    if (!referenceData.last_name) missingKeys.push("reference_last_name");
    if (!referenceData.relationship) missingKeys.push("reference_relationship");
    if (!referenceData.email) missingKeys.push("reference_email");
  }

  const missingSet = new Set(missingKeys);
  const [attemptedConfirm, setAttemptedConfirm] = useState(0); // increment to retrigger
  const [localAttemptedConfirm, setLocalAttemptedConfirm] = useState(0); // Solo para el botón Confirm local
  const [flashActive, setFlashActive] = useState(false);
  const [flashCount, setFlashCount] = useState(0);
  const needsAttention = localAttemptedConfirm > 0 && missingSet.size > 0; // Solo con intento local
  const hasPartialData = missingSet.size > 0;
  const showConfirmReminder = highlightUnconfirmed && !isConfirmed && missingSet.size === 0;
  const emphasizeBorder = needsAttention; // Solo enfatizar cuando haya intentado confirmar localmente y falten campos
  useEffect(() => {
    if (typeof validateSignal === 'number') {
      setAttemptedConfirm(c => c + 1);
    }
  }, [validateSignal]);
  useEffect(() => {
    if (!showConfirmReminder) {
      setNeedsConfirmationHighlight(false);
      return;
    }
    setNeedsConfirmationHighlight(true);
    const timeout = setTimeout(() => setNeedsConfirmationHighlight(false), 2000);
    return () => clearTimeout(timeout);
  }, [showConfirmReminder, confirmReminderSignal]);
  useEffect(() => {
    if (!flashActive) return;
    if (flashCount >= 3) {
      const t = setTimeout(() => setFlashActive(false), 150);
      return () => clearTimeout(t);
    }
    const t = setTimeout(() => setFlashCount(c => c + 1), 1200);
    return () => clearTimeout(t);
  }, [flashActive, flashCount]);
  // Use only animated outline (box-shadow) without changing base border/font/background
  const flashClass = (key: string) => (needsAttention && flashActive && missingSet.has(key)) ? 'flash-border-3x' : '';

  const handleConfirm = () => {
    // First check if there are missing required fields
    if (missingSet.size > 0) {
      // Increment localAttemptedConfirm to show validation errors
      setLocalAttemptedConfirm(c => c + 1);
      setFlashActive(true);
      setFlashCount(0);
      return;
    }

    // Check if warning has already been shown this session
    const warningShown = SessionStorage.getItem(SESSION_STORAGE_KEYS.NO_REFERENCE_WARNING_SHOWN);

    // If no reference and warning hasn't been shown, show warning
    if (!hasReference && !warningShown) {
      setShowNoReferenceWarning(true);
      SessionStorage.setItem(SESSION_STORAGE_KEYS.NO_REFERENCE_WARNING_SHOWN, 'true');
      return;
    }

    // Set editing to false and proceed with confirm
    setIsEditing(false);
    onConfirm();
  };

  const handleEdit = () => {
    setIsEditing(true);
    onEdit();
  };

  const handleWarningClose = () => {
    setShowNoReferenceWarning(false);
    // Set editing to false and proceed with confirm after user acknowledges warning
    setIsEditing(false);
    onConfirm();
  };

  return (
    <div
      className={`mb-4 transition-all duration-300 ${needsConfirmationHighlight ? "shadow-[0_0_0_3px_rgba(239,68,68,0.35)] rounded-lg" : ""
        }`}
    >
      {shouldShowCollapse && hasData && (
        <>
          {/* Desktop Layout - Single row */}
          <div
            className={`hidden md:flex items-center justify-between p-3 rounded-t-lg border ${emphasizeBorder
              ? 'bg-red-50 border-red-500 border-2 shadow-sm'
              : 'bg-gray-50 border-gray-200'
              }`}
          >
            <div
              className="flex items-center gap-2 flex-1 cursor-pointer hover:bg-gray-100 -m-3 p-3 rounded-tl-lg mr-2"
              onClick={onToggleCollapse}
            >
              <button
                type="button"
                className="p-1 hover:bg-gray-200 rounded cursor-pointer"
              >
                {isCollapsed ? (
                  <LuChevronDown size={16} />
                ) : (
                  <LuChevronUp size={16} />
                )}
              </button>
              <span className="font-medium text-gray-700">
                {jobTitle}
              </span>
            </div>
            <div className="flex items-center gap-3">
              {!hasReference && (
                <span className="flex items-center gap-1 text-yellow-600 text-xs bg-yellow-100 px-2 py-1 rounded-full">
                  <LuTriangleAlert size={12} />
                  {tCommon("noReference")}
                </span>
              )}
              <div className="w-20 flex justify-center">
                {validationStatus === 'incomplete' && (
                  <span className="text-red-600 text-xs bg-red-100 px-2 py-1 rounded-full">
                    {tCommon("incomplete")}
                  </span>
                )}
                {validationStatus === 'verify' && (
                  <span className="text-orange-600 text-xs bg-orange-100 px-2 py-1 rounded-full">
                    {tCommon("verify")}
                  </span>
                )}
                {validationStatus === 'confirmed' && (
                  <span className="text-green-600 text-xs bg-green-100 px-2 py-1 rounded-full">
                    {tCommon("confirmed")}
                  </span>
                )}
              </div>
              <button
                type="button"
                onClick={() => setShowDeleteModal(true)}
                className="p-1 hover:bg-red-100 rounded text-red-600"
                title={tCommon("removeExperience")}
              >
                <LuTrash2 size={16} />
              </button>
            </div>
          </div>

          {/* Mobile Layout - Two rows */}
          <div
            className={`md:hidden rounded-t-lg border ${emphasizeBorder
              ? 'bg-red-50 border-red-500 border-2 shadow-sm'
              : 'bg-gray-50 border-gray-200'
              }`}
          >
            {/* First row - Title and controls */}
            <div className="flex items-center justify-between p-3">
              <div
                className="flex items-center gap-2 flex-1 cursor-pointer hover:bg-gray-100 -m-3 p-3 rounded-tl-lg mr-2"
                onClick={onToggleCollapse}
              >
                <button
                  type="button"
                  className="p-1 hover:bg-gray-200 rounded cursor-pointer"
                >
                  {isCollapsed ? (
                    <LuChevronDown size={16} />
                  ) : (
                    <LuChevronUp size={16} />
                  )}
                </button>
                <span className="font-medium text-gray-700">
                  {jobTitle}
                </span>
              </div>
              <button
                type="button"
                onClick={() => setShowDeleteModal(true)}
                className="p-1 hover:bg-red-100 rounded text-red-600"
                title={tCommon("removeExperience")}
              >
                <LuTrash2 size={16} />
              </button>
            </div>

            {/* Second row - Status pills */}
            <div className="flex items-center gap-3 px-3 pb-3">
              {!hasReference && (
                <span className="flex items-center gap-1 text-yellow-600 text-xs bg-yellow-100 px-2 py-1 rounded-full">
                  <LuTriangleAlert size={12} />
                  {tCommon("noReference")}
                </span>
              )}
              <div className="flex justify-start">
                {validationStatus === 'incomplete' && (
                  <span className="text-red-600 text-xs bg-red-100 px-2 py-1 rounded-full">
                    {tCommon("incomplete")}
                  </span>
                )}
                {validationStatus === 'verify' && (
                  <span className="text-orange-600 text-xs bg-orange-100 px-2 py-1 rounded-full">
                    {tCommon("verify")}
                  </span>
                )}
                {validationStatus === 'confirmed' && (
                  <span className="text-green-600 text-xs bg-green-100 px-2 py-1 rounded-full">
                    {tCommon("confirmed")}
                  </span>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      <ConfirmDeleteModal
        isOpen={showDeleteModal}
        onConfirm={() => {
          onRemove();
          setShowDeleteModal(false);
        }}
        onCancel={() => setShowDeleteModal(false)}
        title={jobTitle}
        deleteType="deleteExperience"
      />

      <div
        className={`${shouldShowCollapse && hasData
          ? (emphasizeBorder ? 'border-2 border-red-500 border-t-0 rounded-b-lg bg-whiteshadow-lg' : 'border border-gray-200 border-t-0 rounded-b-lg')
          : (emphasizeBorder ? 'border-2 border-red-500 rounded-lg bg-white shadow-lg' : '')
          } ${isCollapsed && shouldShowCollapse && hasData ? 'hidden' : 'block'}`}
      >
        {/* Validation status pill for expanded view */}
        {!shouldShowCollapse || !hasData ? (
          <div className="flex items-center gap-2 p-2 border-b border-gray-200">
            <span className="font-medium text-gray-700">{jobTitle}</span>
            <div className="w-20 flex justify-center">
              {validationStatus === 'incomplete' && (
                <span className="text-red-600 text-xs bg-red-100 px-2 py-1 rounded-full">
                  {tCommon("incomplete")}
                </span>
              )}
              {validationStatus === 'verify' && (
                <span className="text-orange-600 text-xs bg-orange-100 px-2 py-1 rounded-full">
                  {tCommon("verify")}
                </span>
              )}
              {validationStatus === 'confirmed' && (
                <span className="text-green-600 text-xs bg-green-100 px-2 py-1 rounded-full">
                  {tCommon("confirmed")}
                </span>
              )}
            </div>
          </div>
        ) : null}

        {/* Error banner when validation fails */}
        {needsAttention && (
          <div className="bg-red-100 border-l-4 border-red-500 p-4 m-4 mb-0 rounded-r-md">
            <div className="flex items-center">
              <LuTriangleAlert className="text-red-500 mr-2" size={16} />
              <p className="text-red-700 text-sm font-medium">
                {tCommon("completeAllFields")}
              </p>
            </div>
            <p className="text-red-600 text-xs mt-1 ml-6">
              {tExperience("requiredFields")}
            </p>
          </div>
        )}
        {!isConfirmed && !needsAttention && hasPartialData && localAttemptedConfirm > 0 && (
          <div className="bg-white border-l-4 border-orange-400 p-4 m-4 mb-0 rounded-r-md flex items-start gap-3">
            <LuInfo className="text-orange-500 mt-0.5" size={18} />
            <div>
              <p className="text-orange-700 text-sm font-medium">
                {tCommon("completeToConfirm")}
              </p>
              <p className="text-orange-600 text-xs mt-1">
                {tCommon("confirmToSave")}
              </p>
            </div>
          </div>
        )}
        {showConfirmReminder && !needsAttention && !hasPartialData && (
          <div
            className={`m-4 mb-0 rounded-lg border-2 transition-all duration-300 ${needsConfirmationHighlight
              ? 'border-red-500 bg-red-50/70 shadow-lg'
              : 'border-red-300 bg-red-50/50'
              }`}
          >
            <div className="px-4 py-3 flex items-start gap-3">
              <LuTriangleAlert className="text-red-500 mt-0.5" size={18} />
              <div>
                <p className="text-red-700 text-sm font-semibold">
                  {tCommon("confirmToContinue")}
                </p>
                <p className="text-red-600 text-xs mt-1">
                  {tCommon("confirmBeforeForward")}
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="p-4 space-y-4">
          {/* Job Information */}
          <div className="bg-white border border-[#EDEEFE] rounded-2xl p-4">
            <h3 className="font-normal text-base sm:text-sm mb-4 flex items-center gap-2">
              <svg xmlns="http://www.w3.org/2000/svg" width="17" height="14" viewBox="0 0 17 14" fill="none">
                <path d="M4.81873 12.14V3.83233M10.9125 12.14V3.83233M5.28748 3.37079V1.52464C5.28748 1.01484 5.70721 0.601562 6.22498 0.601562H9.50623C10.024 0.601562 10.4437 1.01484 10.4437 1.52464V3.37079M2.47498 12.6016H13.725C14.7605 12.6016 15.6 11.775 15.6 10.7554V5.21695C15.6 4.19734 14.7605 3.37079 13.725 3.37079H2.47498C1.43944 3.37079 0.599976 4.19734 0.599976 5.21695V10.7554C0.599976 11.775 1.43944 12.6016 2.47498 12.6016Z" stroke="#151515" strokeWidth="1.2" strokeLinecap="round"/>
              </svg>
              {tExperience("jobInformation")}
            </h3>
            <div className="space-y-4">
              <Input
                name={`experiences.${index}.job_title`}
                label={tExperience("jobTitle")}
                placeholder={tExperience("jobTitlePlaceholder")}
                disabled={isConfirmed && !isEditing}
                classNameContainer=""
                className={`bg-[#F9F9F9] ${flashClass('job_title')}`}
                noBorder
                noShadow
                required
              />
              <SelectInput
                name={`experiences.${index}.work_mode`}
                options={optionsJobber?.options?.work_modes || []}
                loading={false}
                label={tExperience("workMode")}
                placeholder={tExperience("workModePlaceholder")}
                disabled={isConfirmed && !isEditing}
                className={`bg-[#F9F9F9] ${flashClass('work_mode')}`}
                noBorder
                noShadow
                required
              />
              <AutoExpandTextArea
                  name={`experiences.${index}.description`}
                  label={tExperience("jobDescription")}
                  placeholder={tExperience("jobDescriptionPlaceholder")}
                  minRows={4}
                  maxRows={8}
                  maxLength={1000}
                  disabled={isConfirmed && !isEditing}     
                  className={`bg-[#F9F9F9] ${flashClass('description')}`}
                  noBorder
                  noShadow
                />
            </div>
          </div>

          {/* Company Information */}
          <div className="bg-white border border-[#EDEEFE] rounded-2xl p-4">
            <h3 className="font-normal text-base sm:text-sm mb-4 flex items-center gap-2">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="15" viewBox="0 0 16 15" fill="none">
                <path d="M8.54442 6.37934H12.0111C12.8201 6.37934 13.2245 6.37934 13.5335 6.53678C13.8053 6.67526 14.0263 6.89623 14.1648 7.16802C14.3222 7.477 14.3222 7.88149 14.3222 8.69045V13.6016M8.54442 6.37934V2.91267C8.54442 2.10371 8.54442 1.69923 8.38699 1.39024C8.2485 1.11845 8.02753 0.897481 7.75574 0.758998C7.44676 0.601562 7.04227 0.601562 6.23331 0.601562H3.63331C2.82434 0.601562 2.41986 0.601562 2.11088 0.758998C1.83909 0.897481 1.61812 1.11845 1.47963 1.39024C1.3222 1.69923 1.3222 2.10371 1.3222 2.91267V13.6016M8.54442 6.37934V13.6016M14.3222 13.6016H15.0444M14.3222 13.6016H8.54442M8.54442 13.6016H1.3222M1.3222 13.6016H0.599976M3.84998 3.49045H6.01664M3.84998 6.37934H6.01664M3.84998 9.26823H6.01664" stroke="#151515" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              {tExperience("companyInformation")}
            </h3>
            <div className="space-y-4">
              <Input
                name={`experiences.${index}.company_name`}
                label={tExperience("companyName")}
                placeholder={tExperience("companyNamePlaceholder")}
                disabled={isConfirmed && !isEditing}
                className={`bg-[#F9F9F9] ${flashClass('company_name')}`}
                noBorder
                noShadow
                required
              />
              <SelectInput
                name={`experiences.${index}.company_industry`}
                options={optionsJobber.options?.company_industry || []}
                loading={false}
                label={tExperience("industry")}
                placeholder={tExperience("industryPlaceholder")}
                disabled={isConfirmed && !isEditing}
                className={`bg-[#F9F9F9] ${flashClass('company_industry')}`}
                noBorder
                noShadow
                required
              />
            </div>
          </div>

          {/* Date Information */}
          <div className="bg-white border border-[#EDEEFE] rounded-2xl p-4">
            <h3 className="font-normal text-base sm:text-sm mb-4 flex items-center gap-2">
              <svg xmlns="http://www.w3.org/2000/svg" width="12" height="14" viewBox="0 0 12 14" fill="none">
                <path d="M1.26664 4.5444H10.6M2.47299 0.601562V1.63025M9.26664 0.601562V1.63013M11.2666 3.43013V10.8016C11.2666 11.7957 10.4707 12.6016 9.48886 12.6016H2.37775C1.39591 12.6016 0.599976 11.7957 0.599976 10.8016V3.43013C0.599976 2.43602 1.39591 1.63013 2.37775 1.63013H9.48886C10.4707 1.63013 11.2666 2.43602 11.2666 3.43013Z" stroke="#151515" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              {tExperience("dateInformation")}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className={flashClass('start_date')}>
                <DatePicker
                  name={`experiences.${index}.start_date`}
                  label={tExperience("startDate")}
                  placeholder={tExperience("startDatePlaceholder")}
                  disabled={isConfirmed && !isEditing}
                  rules={{
                    required: tExperience("startDateRequired"),
                  }}
                  noBorder
                  noShadow
                  className="bg-[#F9F9F9]"
                  required
                />
              </div>
              <DatePicker
                name={`experiences.${index}.end_date`}
                label={tExperience("endDate")}
                placeholder={tExperience("endDatePlaceholder")}
                disabled={isConfirmed && !isEditing}
                minDate={
                  watch(`experiences.${index}.start_date`) &&
                    typeof watch(`experiences.${index}.start_date`) === "string"
                    ? new Date(watch(`experiences.${index}.start_date`) as string)
                    : undefined
                }
                isRangeValidation={true}           
                noBorder
                noShadow
                className="bg-[#F9F9F9]"  
              />
            </div>
          </div>

          {/* Category / Specialty / Skills */}
          <div className="bg-white border border-[#EDEEFE] rounded-2xl p-4">
            <h3 className="font-normal text-base sm:text-sm mb-4 flex items-center gap-2">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path d="M8.90002 5.14841L5.544 8.14839L4.40002 7.12578M12.65 2.89842L12.65 10.3985C12.65 11.6411 11.6427 12.6484 10.4 12.6484H2.90002C1.65738 12.6484 0.650024 11.6411 0.650024 10.3985V2.89842C0.650024 1.65579 1.65738 0.648438 2.90002 0.648438H10.4C11.6427 0.648438 12.65 1.65579 12.65 2.89842Z" stroke="#151515" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              {tExperience("categorySpecialtySkills")}
            </h3>
            <div className="space-y-4">
              <SelectInput
                name={`experiences.${index}.category`}
                options={optionsJobber.options?.category || []}
                loading={false}
                label={tExperience("category")}
                placeholder={tExperience("categoryPlaceholder")}
                disabled={isConfirmed && !isEditing}
                className={`bg-[#F9F9F9] ${flashClass('category')}`}
                noBorder
                noShadow
                required
              />
              <SelectSearchInput
                name={`experiences.${index}.specialization`}
                items={specialtyData?.pages?.flatMap((page) => page.data) || []}
                label={tExperience("specialty")}
                placeholder={tExperience("specialtyPlaceholder")}
                onSearch={setSearchNameSpecialty}
                hasMore={hasNextSpecialtyPage}
                onLoadMore={fetchNextSpecialtyPage}
                loading={isLoadingSpecialty || isFetchingNextSpecialtyPage}
                onResetPage={() => { }}
                className={"w-full bg-[#F9F9F9] " + flashClass('specialization')}
                disabled={isConfirmed && !isEditing}
                defaultItem={specializationDefaultItem}
                noBorder
                noShadow
                required
              />
              <MultiSelectField
                  name={`experiences.${index}.skills_experiences`}
                  options={skillsData?.pages?.flatMap((page) => page.data) || []}
                  label={tExperience("skillsUsed")}
                  placeholder={tExperience("skillsUsedPlaceholder")}
                  useSearch={true}
                  onSearch={setSearchNameSkill}
                  hasMore={hasNextSkillsPage}
                  onLoadMore={fetchNextSkillsPage}
                  loading={isLoadingSkills || isFetchingNextSkillsPage}
                  disabled={isConfirmed && !isEditing}
                  className={flashClass('skills_experiences')}
                  noBorder
                  noShadow
                  bgStyle="bg-[#F9F9F9]"
                  required
                />
            </div>
          </div>
        </div>

        {/* Reference Form */}
        <ReferenceForm
          baseName={`experiences.${index}`}
          disabled={isConfirmed && !isEditing}
          missingSet={missingSet}
          flash={needsAttention && flashActive}
        />
        {refStarted && needsAttention && (
          <div className="px-4 -mt-2 text-xs text-red-600 font-medium flex flex-wrap gap-2 justify-center">
            {['reference_first_name', 'reference_last_name', 'reference_relationship', 'reference_email']
              .filter(f => missingSet.has(f))
              .map(f => {
                const label = f.replace('reference_', '').replace('_', ' ');
                return <span key={f} className="capitalize">{tCommon("missing")} {label}</span>;
              })}
          </div>
        )}

        {/* Confirm/Edit button */}
        {needsAttention && (
          <div className="pb-2 text-xs text-red-600 font-medium animate-pulse text-center">
            {tCommon("fillMissingFields")}
          </div>
        )}
        <div className="flex justify-end p-4 pt-0 pb-6">
          {isConfirmed ? (
            <button
              type="button"
              onClick={handleEdit}
              className="w-full md:w-auto tracking-wide transition-colors duration-200 transform focus:outline-none focus:ring focus:ring-opacity-50 rounded-full px-7 py-2 bg-brand-500 hover:bg-brand-400 focus:bg-brand-400 focus:ring-brand-300 text-white cursor-pointer"
            >
              {tCommon("edit")}
            </button>
          ) : (
            <button
              type="button"
              onClick={handleConfirm}
              className={`w-full md:w-auto tracking-wide transition-colors duration-200 transform focus:outline-none focus:ring focus:ring-opacity-50 rounded-full px-7 py-2 bg-brand-500 hover:bg-brand-400 focus:bg-brand-400 focus:ring-brand-300 text-white cursor-pointer`}
            >
              {tCommon("confirm")}
            </button>
          )}
        </div>
      </div>

      <NoReferenceWarningModal
        isOpen={showNoReferenceWarning}
        onClose={handleWarningClose}
      />
    </div>
  );
};
